import { useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useSite, imgUrl } from "../../lib/site";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Trash, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX = 5 * 1024 * 1024;

export const GalleryManager = () => {
  const { items, reload } = useSite("gallery");
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (f && f.size > MAX) { toast.error("Image must be 5 MB or smaller"); e.target.value = ""; return; }
    setFile(f || null);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    setBusy(true);
    try {
      await api.post("/site/gallery/upload", fd);
      toast.success("Photo published to the gallery");
      setFile(null); setCaption(""); if (inputRef.current) inputRef.current.value = "";
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the website?")) return;
    try { await api.delete(`/site/gallery/${id}`); toast.success("Removed"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 border-zinc-200">
        <form onSubmit={upload} className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label>Photo (JPG, PNG, WEBP · max 5 MB)</Label>
            <Input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} className="mt-1" data-testid="gallery-file-input" />
          </div>
          <div>
            <Label>Caption *</Label>
            <Input required value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Science fair 2026" className="mt-1" data-testid="gallery-caption-input" />
          </div>
          <Button type="submit" disabled={busy} data-testid="gallery-upload-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white">
            <UploadSimple size={16} className="mr-1" />{busy ? "Uploading…" : "Upload"}
          </Button>
        </form>
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((g) => (
          <Card key={g.id} className="overflow-hidden border-zinc-200 group" data-testid={`gallery-admin-${g.id}`}>
            <img src={imgUrl(g.url)} alt={g.caption} className="w-full h-36 object-cover" />
            <div className="p-3 flex items-center gap-2">
              <div className="text-sm flex-1 truncate">{g.caption}</div>
              <button onClick={() => remove(g.id)} className="p-1 text-zinc-500 hover:text-rose-600 transition-colors" data-testid={`gallery-delete-${g.id}`}><Trash size={16} /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
